import { useState, useRef, useEffect, Fragment } from "react";
import { FileText, X, Car, Paperclip, RefreshCw, AlertTriangle } from "lucide-react";
import CroquisEditor from "./CroquisEditor";
import DecodificaCFButton from "./DecodificaCFButton";
import { CIRCOSTANZE } from "../lib/caicid";

const clone = (o) => JSON.parse(JSON.stringify(o));

const Field = ({ label, value, onChange, testId, mono, wide }) => (
  <label className={`flex flex-col gap-1 ${wide ? "col-span-2" : ""}`}>
    <span className="text-[11px] text-slate-400">{label}</span>
    <input
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className={`h-8 rounded-md bg-slate-900/60 border border-slate-700 px-2 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-teal-500/50 ${mono ? "font-mono" : ""}`}
    />
  </label>
);

const SiNo = ({ label, value, onChange, testId }) => (
  <div className="flex items-center gap-3">
    <span className="text-[11px] text-slate-400">{label}</span>
    {["si", "no"].map((v) => (
      <button key={v} type="button" data-testid={`${testId}-${v}`}
        onClick={() => onChange(value === v ? "" : v)}
        className={`px-2 py-0.5 rounded text-xs border transition-colors ${value === v ? "bg-teal-600 border-teal-500 text-white" : "border-slate-600 text-slate-300 hover:bg-slate-800"}`}>
        {v === "si" ? "Sì" : "No"}
      </button>
    ))}
  </div>
);

const SectionTitle = ({ children }) => (
  <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-300 mt-4 mb-2 border-b border-slate-700 pb-1">{children}</h4>
);

const VEIC_FIELDS = [
  { key: "contraente", title: "Contraente / Assicurato", rows: [
    ["cognome", "Cognome", false, false], ["nome", "Nome", false, false],
    ["cf", "Codice Fiscale / Partita IVA", true, true],
    ["indirizzo", "Indirizzo (comune, provincia, via e numero)", false, true],
    ["cap", "C.A.P.", true, false], ["stato", "Stato", false, false],
    ["tel", "N. Tel. o E-mail", false, true],
  ] },
  { key: "veicolo", title: "Veicolo", rows: [
    ["marca", "Marca, Tipo", false, false], ["targa", "N. di targa o telaio", true, false],
    ["stato_imm", "Stato d'immatricolazione", false, true],
  ] },
  { key: "assicurazione", title: "Compagnia d'assicurazione", rows: [
    ["ass_denominazione", "Denominazione", false, true],
    ["polizza", "N. di polizza", true, false], ["carta_verde", "N. di Carta Verde", true, false],
    ["valido_dal", "Valido dal", true, false], ["valido_al", "al", true, false],
    ["agenzia", "Agenzia (o ufficio o intermediario)", false, true],
    ["ag_indirizzo", "Indirizzo agenzia", false, true],
    ["ag_stato", "Stato", false, false], ["ag_tel", "N. Tel. o E-mail", false, false],
  ] },
  { key: "conducente", title: "Conducente", rows: [
    ["cond_cognome", "Cognome", false, false], ["cond_nome", "Nome", false, false],
    ["cond_nascita", "Data di nascita", true, false], ["cond_cf", "Codice Fiscale", true, false],
    ["cond_indirizzo", "Indirizzo (comune, provincia, via e numero)", false, true],
    ["cond_stato", "Stato", false, false], ["cond_tel", "N. Tel. o E-mail", false, false],
    ["patente", "Patente N.", true, false], ["categoria", "Categ. (A, B, ...)", false, false],
    ["patente_scad", "valida fino al", true, false],
  ] },
];

// --- Sezione 10: sagome veicolo (auto + moto, vista dall'alto) e punto d'urto ---
// Frazioni allineate al PDF (box 59mm x 18mm): identiche proporzioni a schermo e su carta.
const URTO = { carCx: 0.3, carWf: 9 / 59, carHf: 14 / 18, carYf: 2 / 18, motoCx: 0.7, motoWf: 5.5 / 59, motoHf: 14 / 18, motoYf: 2 / 18 };

function roundRectPath(ctx, x, y, w, h, r) {
  r = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawCarCanvas(ctx, cx, top, cw, ch) {
  const x = cx - cw / 2;
  ctx.strokeStyle = "#000000"; ctx.fillStyle = "rgba(0,0,0,0.06)"; ctx.lineWidth = 1.8;
  roundRectPath(ctx, x, top, cw, ch, Math.min(cw * 0.35, ch * 0.18)); ctx.fill(); ctx.stroke();
  // Parabrezza (anteriore, in alto): trapezio con lato corto verso il cofano.
  const wTop = top + ch * 0.16, wBot = top + ch * 0.34;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(x + cw * 0.36, wTop); ctx.lineTo(x + cw * 0.64, wTop);
  ctx.lineTo(x + cw * 0.78, wBot); ctx.lineTo(x + cw * 0.22, wBot);
  ctx.closePath(); ctx.stroke();
  // Lunotto posteriore.
  ctx.beginPath(); ctx.moveTo(x + cw * 0.26, top + ch * 0.76); ctx.lineTo(x + cw * 0.74, top + ch * 0.76); ctx.stroke();
}

function drawMotoCanvas(ctx, cx, top, mw, mh) {
  ctx.strokeStyle = "#000000"; ctx.fillStyle = "#000000"; ctx.lineWidth = 1.5;
  // Ruota anteriore (in alto).
  roundRectPath(ctx, cx - mw * 0.22, top, mw * 0.44, mh * 0.14, 1.2); ctx.fill(); ctx.stroke();
  // Manubrio (anteriore): barra trasversale larga.
  ctx.lineWidth = 2.2; ctx.beginPath(); ctx.moveTo(cx - mw * 0.72, top + mh * 0.17); ctx.lineTo(cx + mw * 0.72, top + mh * 0.17); ctx.stroke();
  // Corpo affusolato.
  ctx.lineWidth = 1.5; ctx.fillStyle = "rgba(0,0,0,0.1)";
  roundRectPath(ctx, cx - mw / 2, top + mh * 0.2, mw, mh * 0.6, mw * 0.35); ctx.fill(); ctx.stroke();
  // Ruota posteriore (in basso).
  ctx.fillStyle = "#000000";
  roundRectPath(ctx, cx - mw * 0.22, top + mh * 0.86, mw * 0.44, mh * 0.14, 1.2); ctx.fill(); ctx.stroke();
}

const normUrto = (v) => Array.isArray(v) ? v.filter((p) => p && typeof p.x === "number") : (v && typeof v.x === "number" ? [v] : []);

const UrtoSelector = ({ value, onChange, label }) => {
  const ref = useRef(null);
  const W = 300, H = 92;
  const points = normUrto(value);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, W, H);
    drawCarCanvas(ctx, URTO.carCx * W, URTO.carYf * H, URTO.carWf * W, URTO.carHf * H);
    drawMotoCanvas(ctx, URTO.motoCx * W, URTO.motoYf * H, URTO.motoWf * W, URTO.motoHf * H);
    normUrto(value).forEach((p) => {
      const mx = p.x * W, my = p.y * H;
      ctx.strokeStyle = "#ef4444"; ctx.lineWidth = 2.6; ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(mx - 8, my); ctx.lineTo(mx + 8, my);
      ctx.moveTo(mx, my - 8); ctx.lineTo(mx, my + 8);
      ctx.stroke();
    });
  }, [value]);
  const place = (e) => {
    const r = ref.current.getBoundingClientRect();
    const nx = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
    const ny = Math.max(0, Math.min(1, (e.clientY - r.top) / r.height));
    const idx = points.findIndex((p) => Math.hypot((p.x - nx) * W, (p.y - ny) * H) < 9);
    if (idx >= 0) { const next = points.slice(); next.splice(idx, 1); onChange(next); }
    else onChange([...points, { x: nx, y: ny }]);
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] text-slate-400">Clicca per aggiungere più punti d'urto; clicca su un punto per rimuoverlo</span>
        {points.length > 0 && (
          <button type="button" onClick={() => onChange([])} data-testid={`cai-${label}-urto-clear`}
            className="text-[11px] text-rose-300 hover:underline">Cancella tutti ({points.length})</button>
        )}
      </div>
      <canvas ref={ref} width={W} height={H} onClick={place} data-testid={`cai-${label}-urto-canvas`}
        className="w-full rounded-md bg-white border border-slate-700 cursor-crosshair" style={{ maxWidth: W }} />
    </div>
  );
};

const VehicleForm = ({ label, color, data, setData }) => {
  const upd = (k, v) => setData({ ...data, [k]: v });
  return (
    <div>
      <div className={`rounded-md px-3 py-1.5 font-bold text-sm mb-2 ${color}`}>Veicolo {label}</div>
      {VEIC_FIELDS.map((sec) => (
        <div key={sec.key}>
          <SectionTitle>{sec.title}</SectionTitle>
          <div className="grid grid-cols-2 gap-2">
            {sec.rows.map(([k, lbl, mono, wide]) => (
              <Fragment key={k}>
                <Field label={lbl} value={data[k]} mono={mono} wide={wide}
                  onChange={(v) => upd(k, v)} testId={`cai-${label}-${k}`} />
                {k === "cf" && (
                  <div className="col-span-2 -mt-1">
                    <DecodificaCFButton cf={data.cf} testId={`cai-${label}-decodifica-cf`}
                      onDecode={(d) => setData({ ...data, cognome: data.cognome || d.cognomeCode, nome: data.nome || d.nomeCode })} />
                  </div>
                )}
                {k === "cond_cf" && (
                  <div className="col-span-2 -mt-1">
                    <DecodificaCFButton cf={data.cond_cf} testId={`cai-${label}-cond-decodifica-cf`}
                      onDecode={(d) => setData({ ...data, cond_cognome: data.cond_cognome || d.cognomeCode, cond_nome: data.cond_nome || d.nomeCode, cond_nascita: d.data_nascita_display })} />
                  </div>
                )}
              </Fragment>
            ))}
          </div>
          {sec.key === "assicurazione" && (
            <div className="mt-2"><SiNo label="La polizza copre anche i danni materiali al proprio veicolo" value={data.polizza_copre} onChange={(v) => upd("polizza_copre", v)} testId={`cai-${label}-polizza-copre`} /></div>
          )}
        </div>
      ))}
      <SectionTitle>10. Punto d'urto iniziale del veicolo</SectionTitle>
      <UrtoSelector value={data.urto} label={label} onChange={(v) => upd("urto", v)} />
      <SectionTitle>Danni visibili / Osservazioni</SectionTitle>
      <div className="grid grid-cols-1 gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] text-slate-400">Danni visibili al veicolo</span>
          <textarea value={data.danni_visibili ?? ""} onChange={(e) => upd("danni_visibili", e.target.value)} data-testid={`cai-${label}-danni`} rows={2} className="rounded-md bg-slate-900/60 border border-slate-700 px-2 py-1 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-teal-500/50" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] text-slate-400">Osservazioni</span>
          <textarea value={data.osservazioni ?? ""} onChange={(e) => upd("osservazioni", e.target.value)} data-testid={`cai-${label}-osservazioni`} rows={2} className="rounded-md bg-slate-900/60 border border-slate-700 px-2 py-1 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-teal-500/50" />
        </label>
      </div>
    </div>
  );
};

const EMPTY_F2 = () => ({
  testimoni: [{}, {}], proprietario: {}, feriti: [{}, {}],
});

// Foglio 2 della CAI (retro): testimoni, proprietario, feriti per il veicolo.
const Foglio2Form = ({ label, color, data, setData }) => {
  const f2 = data.foglio2 || EMPTY_F2();
  const put = (patch) => setData({ ...data, foglio2: { ...f2, ...patch } });
  const setArr = (key, i, k, v) => { const arr = [...(f2[key] || [{}, {}])]; arr[i] = { ...(arr[i] || {}), [k]: v }; put({ [key]: arr }); };
  const tid = `cai-${label}-f2`;
  return (
    <div data-testid={tid}>
      <div className={`rounded-md px-3 py-1.5 font-bold text-sm mb-2 ${color}`}>Veicolo {label} — foglio 2</div>
      {[0, 1].map((i) => { const t = (f2.testimoni || [])[i] || {}; return (
        <div key={`t${i}`}>
          <SectionTitle>Testimone {i + 1}</SectionTitle>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Nome e cognome" value={t.nome} wide onChange={(v) => setArr("testimoni", i, "nome", v)} testId={`${tid}-test${i}-nome`} />
            <Field label="Codice fiscale" value={t.cf} mono onChange={(v) => setArr("testimoni", i, "cf", v.toUpperCase())} testId={`${tid}-test${i}-cf`} />
            <Field label="Tel." value={t.tel} mono onChange={(v) => setArr("testimoni", i, "tel", v)} testId={`${tid}-test${i}-tel`} />
            <Field label="Indirizzo" value={t.indirizzo} wide onChange={(v) => setArr("testimoni", i, "indirizzo", v)} testId={`${tid}-test${i}-indirizzo`} />
          </div>
        </div>
      ); })}
      <SectionTitle>Proprietario (solo se diverso dal Contraente/Assicurato)</SectionTitle>
      <div className="grid grid-cols-2 gap-2">
        {[["nome", "Nome e cognome o Denominazione sociale", true], ["indirizzo", "Indirizzo (Comune, via e numero)", true], ["cap", "C.A.P."], ["provincia", "Provincia"], ["nascita", "Luogo e data di nascita", true], ["cf", "Codice fiscale/Partita IVA", true]].map(([k, l, wide]) => (
          <Field key={k} label={l} value={(f2.proprietario || {})[k]} wide={wide} mono={k === "cf"} onChange={(v) => put({ proprietario: { ...(f2.proprietario || {}), [k]: v } })} testId={`${tid}-prop-${k}`} />
        ))}
      </div>
      {[0, 1].map((i) => { const f = (f2.feriti || [])[i] || {}; const set = (k, v) => setArr("feriti", i, k, v); return (
        <div key={`f${i}`}>
          <SectionTitle>Ferito {i + 1}</SectionTitle>
          <div className="flex items-center gap-2 mb-2">
            {[["conducente", "Conducente"], ["passeggero", "Passeggero"], ["pedone", "Pedone"]].map(([v, l]) => (
              <button key={v} type="button" data-testid={`${tid}-fer${i}-${v}`} onClick={() => set("tipo", f.tipo === v ? "" : v)}
                className={`px-2 py-0.5 rounded text-xs border transition-colors ${f.tipo === v ? "bg-teal-600 border-teal-500 text-white" : "border-slate-600 text-slate-300 hover:bg-slate-800"}`}>{l}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Nome e cognome" value={f.nome} wide onChange={(v) => set("nome", v)} testId={`${tid}-fer${i}-nome`} />
            <Field label="Indirizzo (Comune, via e numero)" value={f.indirizzo} wide onChange={(v) => set("indirizzo", v)} testId={`${tid}-fer${i}-indirizzo`} />
            <Field label="Luogo e data di nascita" value={f.nascita} onChange={(v) => set("nascita", v)} testId={`${tid}-fer${i}-nascita`} />
            <Field label="Codice fiscale" value={f.cf} mono onChange={(v) => set("cf", v.toUpperCase())} testId={`${tid}-fer${i}-cf`} />
            <div className="col-span-2"><SiNo label="Ricovero Pronto Soccorso" value={f.ricovero} onChange={(v) => set("ricovero", v)} testId={`${tid}-fer${i}-ricovero`} /></div>
            <Field label="Nome Pronto Soccorso / Comune" value={f.ps} wide onChange={(v) => set("ps", v)} testId={`${tid}-fer${i}-ps`} />
          </div>
        </div>
      ); })}
    </div>
  );
};

// Modale form editabile del modulo CAI/CID. onGenera({ form, intestazione }).
export default function CaiCidEditModal({ initialForm, onClose, onGenera, onSaveDraft, onSaveAllegato, onReloadFromPratica }) {
  const [form, setForm] = useState(() => clone(initialForm));
  const [busy, setBusy] = useState("");
  const [reloadConfirm, setReloadConfirm] = useState(false);
  const s = form.sinistro, alt = form.altre;
  const setS = (k, v) => setForm({ ...form, sinistro: { ...form.sinistro, [k]: v } });
  const setAlt = (k, v) => setForm({ ...form, altre: { ...form.altre, [k]: v } });
  const toggleCirc = (i, side) => {
    const arr = form.circostanze.map((c, idx) => idx === i ? { ...c, [side]: !c[side] } : c);
    setForm({ ...form, circostanze: arr });
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-3" data-testid="cai-edit-modal">
      <div className="relative bg-[#121824] border border-slate-700 rounded-lg shadow-2xl w-full max-w-5xl flex flex-col max-h-[94vh]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
          <div className="font-head font-semibold text-slate-100 flex items-center gap-2"><FileText className="h-4 w-4 text-blue-400" /> Compila modulo CAI/CID</div>
          <div className="flex items-center gap-2">
            {onReloadFromPratica && (
              <button type="button" onClick={() => setReloadConfirm(true)} data-testid="cai-reload-from-pratica"
                className="flex items-center gap-1.5 text-xs rounded-md border border-amber-500/50 text-amber-300 hover:bg-amber-500/10 px-2.5 py-1.5 transition-colors">
                <RefreshCw className="h-3.5 w-3.5" /> Aggiorna dati dalla pratica
              </button>
            )}
            <button type="button" onClick={onClose} data-testid="cai-edit-close" className="text-slate-400 hover:text-slate-100"><X className="h-5 w-5" /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {/* Dati sinistro */}
          <SectionTitle>Dati del sinistro</SectionTitle>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Field label="Data incidente" value={s.data} mono onChange={(v) => setS("data", v)} testId="cai-sin-data" />
            <Field label="Ora" value={s.ora} mono onChange={(v) => setS("ora", v)} testId="cai-sin-ora" />
            <div className="col-span-2 md:col-span-2"><Field label="Luogo (comune, provincia, via e numero)" value={s.luogo} wide onChange={(v) => setS("luogo", v)} testId="cai-sin-luogo" /></div>
          </div>
          <div className="flex flex-wrap gap-5 mt-2">
            <SiNo label="Feriti anche se lievi" value={s.feriti} onChange={(v) => setS("feriti", v)} testId="cai-sin-feriti" />
            <SiNo label="Danni a veicoli oltre A o B" value={s.danni_veicoli} onChange={(v) => setS("danni_veicoli", v)} testId="cai-sin-danniveic" />
            <SiNo label="Danni a oggetti diversi dai veicoli" value={s.danni_oggetti} onChange={(v) => setS("danni_oggetti", v)} testId="cai-sin-danniogg" />
          </div>
          <label className="flex flex-col gap-1 mt-2">
            <span className="text-[11px] text-slate-400">Testimoni (cognome, nome, indirizzo, telefono)</span>
            <textarea value={s.testimoni ?? ""} onChange={(e) => setS("testimoni", e.target.value)} data-testid="cai-sin-testimoni" rows={2} className="rounded-md bg-slate-900/60 border border-slate-700 px-2 py-1 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-teal-500/50" />
          </label>

          {/* Veicoli A / B */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-2">
            <VehicleForm label="A" color="bg-cyan-500/20 text-cyan-200 border border-cyan-500/40" data={form.A} setData={(d) => setForm({ ...form, A: d })} />
            <VehicleForm label="B" color="bg-yellow-500/20 text-yellow-200 border border-yellow-500/40" data={form.B} setData={(d) => setForm({ ...form, B: d })} />
          </div>

          {/* Circostanze */}
          <SectionTitle>Circostanze dell'incidente (spunta per A e B)</SectionTitle>
          <div className="rounded-md border border-slate-700 overflow-hidden">
            <div className="grid grid-cols-[40px_1fr_40px] items-center bg-slate-800/60 text-[11px] font-semibold text-slate-300 px-2 py-1">
              <span className="text-center">A</span><span></span><span className="text-center">B</span>
            </div>
            {form.circostanze.map((c, i) => (
              <div key={i} className="grid grid-cols-[40px_1fr_40px] items-center px-2 py-1 border-t border-slate-800 text-sm text-slate-200">
                <span className="flex justify-center">
                  <input type="checkbox" checked={c.a} onChange={() => toggleCirc(i, "a")} data-testid={`cai-circ-${i + 1}-a`} className="h-4 w-4 accent-cyan-500" />
                </span>
                <span className="px-2"><span className="text-slate-500 mr-1">{i + 1}.</span>{c.t}</span>
                <span className="flex justify-center">
                  <input type="checkbox" checked={c.b} onChange={() => toggleCirc(i, "b")} data-testid={`cai-circ-${i + 1}-b`} className="h-4 w-4 accent-yellow-500" />
                </span>
              </div>
            ))}
          </div>
          {(() => {
            const aCount = form.circostanze.filter((c) => c.a).length;
            const bCount = form.circostanze.filter((c) => c.b).length;
            return (
              <div className="grid grid-cols-[40px_1fr_40px] items-center px-2 py-1.5 mt-1 rounded-md bg-slate-800/40 text-[11px] text-slate-300" data-testid="cai-circ-totali">
                <span className="flex items-center justify-center gap-1">
                  <span data-testid="cai-circ-tot-a" className="inline-flex items-center justify-center min-w-[22px] h-6 px-1 rounded border border-cyan-500/50 bg-slate-900/60 text-cyan-200 font-mono font-bold text-sm">{aCount}</span>
                  <span className="text-slate-400 text-base leading-none">←</span>
                </span>
                <span className="text-center">indicare il numero di caselle barrate con una croce</span>
                <span className="flex items-center justify-center gap-1">
                  <span className="text-slate-400 text-base leading-none">→</span>
                  <span data-testid="cai-circ-tot-b" className="inline-flex items-center justify-center min-w-[22px] h-6 px-1 rounded border border-yellow-500/50 bg-slate-900/60 text-yellow-200 font-mono font-bold text-sm">{bCount}</span>
                </span>
              </div>
            );
          })()}
          <SectionTitle>13. Grafico dell'incidente al momento dell'urto</SectionTitle>
          <label className="flex flex-col gap-1 mb-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-slate-400">Dinamica del sinistro</span>
              <div className="flex items-center gap-1" data-testid="cai-dinamica-font-controls">
                <span className="text-[11px] text-slate-400 mr-1">Dimensione testo</span>
                <button type="button" data-testid="cai-dinamica-font-dec" title="Riduci dimensione testo"
                  onClick={() => setForm({ ...form, dinamica_font: Math.max(10, (Number(form.dinamica_font) || 14) - 1) })}
                  className="h-6 w-6 flex items-center justify-center rounded border border-slate-600 text-slate-200 hover:bg-slate-800 text-sm font-semibold">−</button>
                <span data-testid="cai-dinamica-font-value" className="min-w-[36px] text-center text-[11px] font-mono text-slate-200">{Math.max(10, Math.min(18, Number(form.dinamica_font) || 14))}pt</span>
                <button type="button" data-testid="cai-dinamica-font-inc" title="Aumenta dimensione testo"
                  onClick={() => setForm({ ...form, dinamica_font: Math.min(18, (Number(form.dinamica_font) || 14) + 1) })}
                  className="h-6 w-6 flex items-center justify-center rounded border border-slate-600 text-slate-200 hover:bg-slate-800 text-sm font-semibold">+</button>
              </div>
            </div>
            <textarea value={form.dinamica ?? ""} onChange={(e) => setForm({ ...form, dinamica: e.target.value })} data-testid="cai-dinamica" rows={3} style={{ fontSize: `${Math.max(10, Math.min(18, Number(form.dinamica_font) || 14))}pt`, lineHeight: 1.3 }} className="rounded-md bg-slate-900/60 border border-slate-700 px-2 py-1 text-slate-100 focus:outline-none focus:ring-1 focus:ring-teal-500/50" />
          </label>
          <CroquisEditor value={form.croquis} drawValue={form.croquis_draw} shapes={form.croquis_shapes} dinamica={form.dinamica} dinamicaFont={form.dinamica_font} onChange={({ croquis, draw, shapes }) => setForm({ ...form, croquis, croquis_draw: draw, croquis_shapes: shapes })} />

          {/* Altre informazioni */}
          <SectionTitle>Altre informazioni</SectionTitle>
          <div className="flex flex-wrap items-center gap-6">
            <SiNo label="Intervento dell'Autorità" value={alt.autorita} onChange={(v) => setAlt("autorita", v)} testId="cai-alt-autorita" />
            <div className="flex items-center gap-3">
              <span className="text-[11px] text-slate-400">Quale Autorità</span>
              {[["cc", "CC"], ["ps", "P.S."], ["vvuu", "VV.UU."]].map(([k, lbl]) => (
                <button key={k} type="button" data-testid={`cai-alt-${k}`} onClick={() => setAlt(k, !alt[k])}
                  className={`px-2 py-0.5 rounded text-xs border transition-colors ${alt[k] ? "bg-teal-600 border-teal-500 text-white" : "border-slate-600 text-slate-300 hover:bg-slate-800"}`}>{lbl}</button>
              ))}
            </div>
            <SiNo label="Applicazione Legge 39/2000 (indennizzo diretto)" value={alt.legge39} onChange={(v) => setAlt("legge39", v)} testId="cai-alt-legge39" />
          </div>
          <SectionTitle>Foglio 2 — Testimoni, proprietario e feriti</SectionTitle>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Foglio2Form label="A" color="bg-cyan-500/20 text-cyan-200 border border-cyan-500/40" data={form.A} setData={(d) => setForm({ ...form, A: d })} />
            <Foglio2Form label="B" color="bg-yellow-500/20 text-yellow-200 border border-yellow-500/40" data={form.B} setData={(d) => setForm({ ...form, B: d })} />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 px-4 py-3 border-t border-slate-700">
          <button type="button" onClick={onClose} data-testid="cai-edit-cancel" className="text-sm rounded-md border border-slate-600 text-slate-200 hover:bg-slate-800 px-3 py-1.5">Annulla</button>
          {onSaveDraft && (
            <button type="button" data-testid="cai-salva-bozza" disabled={!!busy}
              onClick={async () => { setBusy("draft"); try { await onSaveDraft(form); } finally { setBusy(""); } }}
              className="text-sm rounded-md border border-teal-500/50 text-teal-200 hover:bg-teal-500/10 px-3 py-1.5 disabled:opacity-50">
              {busy === "draft" ? "Salvataggio…" : "Salva bozza"}
            </button>
          )}
          {onSaveAllegato && (
            <button type="button" data-testid="cai-salva-allegato" disabled={!!busy}
              onClick={async () => { setBusy("att"); try { await onSaveAllegato({ form }); } finally { setBusy(""); } }}
              className="flex items-center gap-2 text-sm rounded-md border border-amber-500/50 text-amber-300 hover:bg-amber-500/10 px-3 py-1.5 disabled:opacity-50">
              <Paperclip className="h-4 w-4" /> {busy === "att" ? "Salvataggio…" : "Salva come allegato"}
            </button>
          )}
          <button type="button" data-testid="cai-genera-pdf" onClick={() => onGenera({ form })}
            className="flex items-center gap-2 text-sm rounded-md bg-blue-500 hover:bg-blue-600 text-white font-semibold px-3 py-1.5">
            <Car className="h-4 w-4" /> Genera PDF
          </button>
        </div>

        {reloadConfirm && (
          <div className="absolute inset-0 z-[90] flex items-center justify-center bg-black/70 p-4 rounded-lg" data-testid="cai-reload-confirm">
            <div className="bg-[#121824] border border-amber-500/40 rounded-lg shadow-2xl w-full max-w-md p-5">
              <div className="flex items-center gap-2 text-amber-300 font-head font-semibold"><AlertTriangle className="h-5 w-5" /> Aggiorna dati dalla pratica</div>
              <p className="text-sm text-slate-300 mt-3">Tutti i dati del modulo CAI verranno ricaricati dalla pratica (cliente, controparte, veicoli, compagnie, conducenti). Le eventuali modifiche manuali effettuate nella CAI verranno sostituite con i dati aggiornati della pratica. Continuare?</p>
              <div className="flex justify-end gap-2 mt-5">
                <button type="button" onClick={() => setReloadConfirm(false)} data-testid="cai-reload-cancel" className="text-sm rounded-md border border-slate-600 text-slate-200 hover:bg-slate-800 px-3 py-1.5">Annulla</button>
                <button type="button" data-testid="cai-reload-confirm-btn"
                  onClick={() => { const fresh = onReloadFromPratica && onReloadFromPratica(); if (fresh) setForm(clone(fresh)); setReloadConfirm(false); }}
                  className="flex items-center gap-1.5 text-sm rounded-md bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold px-3 py-1.5">
                  <RefreshCw className="h-4 w-4" /> Aggiorna dati
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
